import restaurantConfig from '../config/restaurantConfig';
import { Restaurant, Address, Contact, PaymentMethodDto, OpeningHours } from '../types';

/**
 * Utility functions for accessing restaurant configuration
 */

/**
 * Get the restaurant information
 */
export const getRestaurantInfo = (restaurant?: Restaurant | null) => {
  return restaurant || restaurantConfig.restaurant;
};

/**
 * Get the restaurant name
 */
export const getRestaurantName = (restaurant?: Restaurant | null) => {
  return restaurant?.name || restaurantConfig.restaurant.name;
};

/**
 * Get the restaurant description
 */
export const getRestaurantDescription = (restaurant?: Restaurant | null) => {
  return restaurant?.description || restaurantConfig.restaurant.description;
};

/**
 * Get the restaurant address
 */
export const getRestaurantAddress = (restaurant?: Restaurant | null): Address | undefined => {
  return restaurant?.address;
};

/**
 * Get the restaurant contact information
 */
export const getRestaurantContact = (restaurant?: Restaurant | null): Contact | undefined => {
  return restaurant?.contact;
};

/**
 * Get the restaurant opening hours
 */
export const getRestaurantOpeningHours = (restaurant?: Restaurant | null): OpeningHours[] => {
  return restaurant?.openingHours || restaurantConfig.restaurant.openingHours;
};

/**
 * Get the restaurant logo URL
 */
export const getRestaurantLogo = (restaurant?: Restaurant | null) => {
  return restaurant?.logo || restaurantConfig.images.logo;
};

/**
 * Get the restaurant banner URL
 */
export const getRestaurantBanner = (restaurant?: Restaurant | null) => {
  return restaurant?.coverImage || restaurantConfig.images.banner;
};

/**
 * Get the restaurant payment methods
 */
export const getPaymentMethods = (restaurant?: Restaurant | null): PaymentMethodDto[] => {
  return restaurant?.paymentMethods || [];
};

/**
 * Get the restaurant delivery options
 */
export const getDeliveryOptions = (restaurant?: Restaurant | null) => {
  return restaurant?.deliveryOptions || restaurantConfig.deliveryOptions;
};

/**
 * Get the restaurant SEO information
 */
export const getSeoInfo = () => {
  return restaurantConfig.seo;
};

/**
 * Check if the restaurant is currently open
 * @param restaurant - Optional dynamic restaurant data
 * @returns boolean indicating if the restaurant is open
 */
export const isRestaurantOpen = (restaurant?: Restaurant | null): boolean => {
  // If we have the isOpen attribute from the backend, use it as it's more accurate
  // (considers manual pauses, manager availability, etc.)
  if (restaurant && typeof restaurant.isOpen === 'boolean') {
    return restaurant.isOpen;
  }

  const now = new Date();
  const currentDay = now.getDay(); // 0-6 (Sunday-Saturday)
  const currentHour = now.getHours();
  const currentMinute = now.getMinutes();
  const currentTimeInMinutes = currentHour * 60 + currentMinute;

  const openingHours = restaurant?.openingHours || restaurantConfig.restaurant.openingHours;
  const todayHours = (openingHours as any[]).find((h: { dayOfWeek: number }) => h.dayOfWeek === currentDay);
  if (!todayHours || !todayHours.timeSlots || todayHours.timeSlots.length === 0) return false;

  // Check if current time is within any of the time slots
  for (const timeSlot of todayHours.timeSlots) {
    const [openHour, openMinute] = timeSlot.open.split(':').map(Number);
    const [closeHour, closeMinute] = timeSlot.close.split(':').map(Number);

    const openTimeInMinutes = openHour * 60 + openMinute;
    let closeTimeInMinutes = closeHour * 60 + closeMinute;

    // Handle slots that cross midnight (e.g., 18:00 - 02:00)
    if (closeTimeInMinutes <= openTimeInMinutes) {
      closeTimeInMinutes += 24 * 60;
    }

    // Check if current time is between opening and closing hours
    if (currentTimeInMinutes >= openTimeInMinutes && currentTimeInMinutes < closeTimeInMinutes) {
      return true;
    }

    // Also check if current time is "early morning" part of a slot that started yesterday
    if (currentTimeInMinutes + 24 * 60 >= openTimeInMinutes && currentTimeInMinutes + 24 * 60 < closeTimeInMinutes) {
      return true;
    }
  }

  // Also check yesterday's slots for ones that might still be open today (cross-midnight)
  const yesterday = (currentDay + 6) % 7;
  const yesterdayHours = (openingHours as any[]).find((h: { dayOfWeek: number }) => h.dayOfWeek === yesterday);
  if (yesterdayHours && yesterdayHours.timeSlots) {
    for (const timeSlot of yesterdayHours.timeSlots) {
      const [openHour, openMinute] = timeSlot.open.split(':').map(Number);
      const [closeHour, closeMinute] = timeSlot.close.split(':').map(Number);
      
      const openTimeInMinutes = openHour * 60 + openMinute;
      const closeTimeInMinutes = closeHour * 60 + closeMinute;

      if (closeTimeInMinutes <= openTimeInMinutes) {
        // This slot crosses midnight. Check if current time (today) is before closing time.
        if (currentTimeInMinutes < closeTimeInMinutes) {
          return true;
        }
      }
    }
  }

  return false;
};

/**
 * Get the next opening time of the restaurant
 * @param restaurant - Optional dynamic restaurant data
 * @returns string with the next opening time (e.g., "Hoje às 11:00" or "Segunda às 11:00")
 */
export const getNextOpeningTime = (restaurant?: Restaurant | null): string => {
  const now = new Date();
  const currentDay = now.getDay(); // 0-6 (Sunday-Saturday)
  const currentHour = now.getHours();
  const currentMinute = now.getMinutes();
  const currentTimeInMinutes = currentHour * 60 + currentMinute;

  const openingHours = restaurant?.openingHours || restaurantConfig.restaurant.openingHours;

  // Check if restaurant opens later today
  const todayHours = (openingHours as any[]).find((h: { dayOfWeek: number }) => h.dayOfWeek === currentDay);
  if (todayHours && todayHours.timeSlots && todayHours.timeSlots.length > 0) {
    // Sort today's time slots by opening time
    const sortedTimeSlots = [...todayHours.timeSlots].sort((a, b) => {
      const [aHour, aMinute] = a.open.split(':').map(Number);
      const [bHour, bMinute] = b.open.split(':').map(Number);
      return (aHour * 60 + aMinute) - (bHour * 60 + bMinute);
    });

    // Find the next time slot that opens later today
    for (const timeSlot of sortedTimeSlots) {
      const [openHour, openMinute] = timeSlot.open.split(':').map(Number);
      const openTimeInMinutes = openHour * 60 + openMinute;

      if (openTimeInMinutes > currentTimeInMinutes) {
        return `Hoje às ${timeSlot.open}`;
      }
    }
  }

  // Find next opening day
  for (let i = 1; i <= 7; i++) {
    const nextDay = (currentDay + i) % 7;
    const nextDayHours = (openingHours as any[]).find((h: { dayOfWeek: number }) => h.dayOfWeek === nextDay);
    if (nextDayHours && nextDayHours.timeSlots && nextDayHours.timeSlots.length > 0) {
      // Sort next day's time slots by opening time
      const sortedTimeSlots = [...nextDayHours.timeSlots].sort((a, b) => {
        const [aHour, aMinute] = a.open.split(':').map(Number);
        const [bHour, bMinute] = b.open.split(':').map(Number);
        return (aHour * 60 + aMinute) - (bHour * 60 + bMinute);
      });

      // Return the first time slot of the next day
      const days = ['Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado'];
      return `${days[nextDay]} às ${sortedTimeSlots[0].open}`;
    }
  }

  return '';
};

/**
 * Format a price in Brazilian Real
 * @param price - The price to format
 * @returns Formatted price string (e.g., "R$ 15,90")
 */
export const formatPrice = (price: number): string => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(price);
};

/**
 * Get the day name from a day of week number
 * @param day - Day of week (0-6, where 0 is Sunday)
 * @returns Day name in Portuguese
 */
export const getDayName = (day: number): string => {
  const days = ['Domingo', 'Segunda-feira', 'Terça-feira', 'Quarta-feira', 'Quinta-feira', 'Sexta-feira', 'Sábado'];
  return days[day];
};

// Export the entire config for direct access if needed
export const getFullConfig = () => {
  return restaurantConfig;
};
